import styles from "./index.module.css";

import React from "react";
import BaseComponent, {executeComponentCallback} from 'Core/components/BaseComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {getGlobalActions} from 'Core/helpers/redux';
import Icon from 'Core/components/display/Icon';
import {getArray} from 'Core/helpers/data';
import Label from 'Core/components/display/Label';
import {icon_font_error_symbol, messages_default_auto_hide_after} from 'Config/app';
import {Tooltip} from 'Core/components/global/Tooltip';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import {sortBy} from 'lodash';
import Spinner from 'Core/components/display/Spinner';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions();

class DynamicCampaignTemplate extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: 'DynamicCampaignTemplate',
			domPrefix: 'dynamic-campaign-template',
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClick', 'onReload', ...Object.keys(allActions)],
		});
		
		// Dialog methods
		this.openErrorDialog = this.openErrorDialog.bind(this);
	}


	// Dialog methods ---------------------------------------------------------------------------------------------------
	/**
	 * Open the error message dialog
	 */
	openErrorDialog() {
		const {addErrorMessageAction, errors} = this.props;

		addErrorMessageAction(
			(
				<ul>
					{getArray(errors).map((mdError, idx) =>
						<Label
							key={idx}
							element="li"
							content={this.t(
								mdError.errorType,
								'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage.errors.types',
								'',
								{field: this.t(
									mdError.errorValue,
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage.errors.fields'
								)}
							)}
							supportHtml={true}
						/>
					)}
				</ul>
			),
			messages_default_auto_hide_after,
			'',
			undefined,
			undefined,
			undefined,
			true,
			{
				id: 'publish-dynamic-campaign-template-error-dialog',
				closeOnEscape: true,
				closeOnClickOutside: false,
				hideCloseBtn: true,
			},
			{
				title: this.t(
					'template_dialog_title',
					'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage.errors'
				),
				allowHtml: true,
				alignContent: 'left'
			}
		)
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {className, errors} = this.props;
		/** @type {MessageDefinitionDataObject} */
		const template = this.getProp('template');
		/** @type {MessageDefinitionChannelDataObject[]} */
		const channels = sortBy(getArray(template, 'channels'), ['priority']);
		
		return (
			typeof template === 'undefined' ?
				<div
					id={this.getDomId()}
					className={`${this.getOption('domPrefix')} ${styles['wrapper']} ${className} card action-card`}
					onClick={e => executeComponentCallback(this.props.onReload, e)}
				>
					<Tooltip
						tag="div"
						title={this.t('failedToLoadError')}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
					>
						<div className={`card-content ${styles['content']}`}>
							<Icon symbol={icon_font_error_symbol} />
							<Label
								element="p"
								content={this.t(
									'pings_section_template',
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
								)}
							/>
						</div>
					</Tooltip>
				</div>
			: template === null ?
				<div
					id={this.getDomId()}
					className={`${this.getOption('domPrefix')} ${styles['wrapper']} ${className} card action-card no-action`}
				>
					<div className={`card-content ${styles['content']}`}>
						<Spinner size={50} />
						<Label
							element="p"
							content={this.t(
								'pings_section_template',
								'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
							)}
						/>
					</div>
				</div>
			:
				<div 
					id={this.getDomId()} 
					className={`${this.getOption('domPrefix')} ${styles['wrapper']} ${className} card action-card`}
					onClick={e => executeComponentCallback(this.props.onClick, e)}
				>
					<div className={`card-content ${styles['content']}`}>
						<Icon symbol="book" />
						<Label 
							element="p" 
							content={this.t(
								'pings_section_template',
								'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
							)} 
						/>
						<div className={`description ${styles['description']}`}>
							<div className={`${styles['channels']} channels`}>
								{channels.filter(c => !!c.channelId).map((c, idx) =>
									<span key={c.channelId} className={`${styles['channel']}`}>
										<Label
											element="span"
											content={c.channelName}
											tooltip={(idx === 0 ? 
												this.t(
													'mainChannelField',
													(
														'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage' +
														'.MessageDefinitionPopup.ChannelsTab'
													)
												) : ''
											)}
										/>
										{idx <= (channels.length - 2) ? 
											<Icon symbol="chevron-right" className={styles['arrow']} /> 
											: null
										}
									</span>
								)}
							</div>
						</div>
		
						{!Array.isArray(errors) || errors.length === 0 ? null :
							<Tooltip
								tag="div"
								title={this.t(
									'message_definition_error_tooltip', 
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage.messageDefinition'
								)}
								size="small"
								position="top-center"
								arrow={true}
								interactive={false}
							>
								<Button
									icon={icon_font_error_symbol}
									displayType={BUTTON_DISPLAY_TYPE.SOLID}
									displayStyle={BUTTON_STYLE.ERROR}
									onClick={e => {
										e.stopPropagation();
										this.openErrorDialog();
									}}
								/>
							</Tooltip>
						}
					</div>
				</div>
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
DynamicCampaignTemplate.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	
	/** @type {MessageDefinitionDataObject} */
	template: PropTypes.object,
	errors: PropTypes.arrayOf(PropTypes.shape({
		errorType: PropTypes.string.isRequired,
		errorValue: PropTypes.string.isRequired,
	})),
	
	onClick: PropTypes.func,
	onReload: PropTypes.func,
};

/**
 * Define component default values for own props
 */
DynamicCampaignTemplate.defaultProps = {
	id: '',
	className: '',
	errors: [],
};

export default connect(null, allActions, null, {forwardRef: true})(DynamicCampaignTemplate);